import { action, makeObservable, observable, reaction } from 'mobx';
import {
  DEFAULT_MINES_COUNT,
  MAX_BOX_COUNT,
  MINES_COUNT_LIMIT,
  MINES_FIELD_STATE,
} from '../minesCore/constants';
import { minesGameService } from '../minesService/gameService';
import { autoBetStore } from '../../../store/autoBetStore';
import { userInfoStore } from '../../../store/userInfoStore';
import { freeBetStore } from '../../../store/freeBetStore';

class MinesBetStore {
  minesCount = DEFAULT_MINES_COUNT;

  diamondsCount = MAX_BOX_COUNT - DEFAULT_MINES_COUNT;

  minesBox = this.generateUniqueIds(new Array(MAX_BOX_COUNT).fill(MINES_FIELD_STATE.UnOpened));

  isDoSpinStart = false;

  nonSelectedBoxIndex = new Array(MAX_BOX_COUNT)
    .fill(MINES_FIELD_STATE.UnOpened)
    .map((el, index) => index);

  cashOutWin = null;

  isAllowCashOut = false;

  openedFields = [];

  isBetsClosed = false;

  winAmount = null;

  winOdd = null;

  odds = null;

  oddsByBombCount = [];

  activeOdd = null;

  currentActiveOdd = null;

  oddsForView = [];

  isLose = false;

  selectedFieldsIndex = [];

  isAllowSendResultReport = false;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      isLose: observable,
      minesBox: observable,
      winAmount: observable,
      activeOdd: observable,
      currentActiveOdd: observable,
      odds: observable,
      minesCount: observable,
      cashOutWin: observable,
      oddsForView: observable,
      openedFields: observable,
      isBetsClosed: observable,
      isDoSpinStart: observable,
      diamondsCount: observable,
      isAllowCashOut: observable,
      oddsByBombCount: observable,
      nonSelectedBoxIndex: observable,
      selectedFieldsIndex: observable,
      isAllowSendResultReport: observable,
      setIsLose: action.bound,
      setWinOdd: action.bound,
      setMinesBox: action.bound,
      setActiveOdd: action.bound,
      setWinAmount: action.bound,
      setCashOutWin: action.bound,
      setMinesCount: action.bound,
      setOdds: action.bound,
      setOddsForView: action.bound,
      setIsBetsClosed: action.bound,
      setOpenedFields: action.bound,
      setIsDoSpinStart: action.bound,
      setDiamondsCount: action.bound,
      handleAutoSelect: action.bound,
      updateOddsForView: action.bound,
      setCurrentActiveOdd: action.bound,
      setIsAllowCashOut: action.bound,
      setOddsByBombCount: action.bound,
      updateDiamondsCount: action.bound,
      setNonSelectedBoxIndex: action.bound,
      setSelectedFieldsIndex: action.bound,
      handleIncreaseMinesCount: action.bound,
      resetNonSelectedBoxIndex: action.bound,
      updateSelectedFieldsIndex: action.bound,
      setIsAllowSendResultReport: action.bound,
    });

    reaction(
      () => this.isDoSpinStart,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );

    reaction(
      () => autoBetStore.isAutoBetOn,
      (value) => {
        if (!value) {
          this.setSelectedFieldsIndex([]);
        }
      }
    );
    reaction(
      () => this.minesCount,
      (value) => {
        if (MAX_BOX_COUNT - value < this.selectedFieldsIndex.length) {
          this.nonSelectedBoxIndex = new Array(MAX_BOX_COUNT)
            .fill(MINES_FIELD_STATE.UnOpened)
            .map((el, index) => index);
          this.selectedFieldsIndex = [];
        }
      }
    );
  }

  setOpenedFields(fields) {
    this.openedFields = fields;
  }

  setIsAllowSendResultReport(bool) {
    this.isAllowSendResultReport = bool;
  }

  setSelectedFieldsIndex(fieldsIndexArr) {
    this.selectedFieldsIndex = fieldsIndexArr;
  }

  updateSelectedFieldsIndex(fieldIndex) {
    const selectedFieldsIndex = this.selectedFieldsIndex.includes(fieldIndex)
      ? this.selectedFieldsIndex.filter((el) => el !== fieldIndex)
      : [...this.selectedFieldsIndex, fieldIndex];
    if (this.minesCount + selectedFieldsIndex.length <= MINES_COUNT_LIMIT.maxBet + 1) {
      this.selectedFieldsIndex = selectedFieldsIndex;
    }
  }

  setOddsForView(arr) {
    this.oddsForView = arr;
  }

  setActiveOdd(activeOdd) {
    this.activeOdd = activeOdd;
  }

  setCurrentActiveOdd(currentOdd) {
    this.currentActiveOdd = currentOdd;
  }

  setOdds(odd) {
    this.odds = odd;
  }

  setIsLose(bool) {
    this.isLose = bool;
  }

  setWinOdd(winOdd) {
    this.winOdd = winOdd;
  }

  setOddsByBombCount(odds) {
    this.oddsByBombCount = odds;
    this.setOddsForView(odds.slice(0, 3));
    this.setActiveOdd(odds[0]);
  }

  updateOddsForView(odd, isInitial) {
    const currentActiveOddIndex = this.oddsByBombCount.findIndex((item) => item === odd);
    if (isInitial) {
      if (currentActiveOddIndex > 2) {
        this.setOddsForView(
          this.oddsByBombCount.slice(currentActiveOddIndex - 2, currentActiveOddIndex + 3)
        );
      } else {
        this.setOddsForView([...this.oddsByBombCount.slice(0, 3 + currentActiveOddIndex)]);
      }
      return;
    }
    this.setActiveOdd(this.oddsByBombCount[currentActiveOddIndex + 1]);
    if (this.oddsForView.length < 5) {
      this.setOddsForView([...this.oddsForView, this.oddsByBombCount[this.oddsForView.length]]);
    } else {
      this.setOddsForView(
        this.oddsByBombCount.slice(currentActiveOddIndex - 1, currentActiveOddIndex + 4)
      );
    }
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setIsAllowCashOut(bool) {
    this.isAllowCashOut = bool;
  }

  setCashOutWin(cashOutWin) {
    this.cashOutWin = cashOutWin;
  }

  setNonSelectedBoxIndex(fieldIndexArr) {
    this.nonSelectedBoxIndex = fieldIndexArr.reduce((acc, current, index) => {
      if (current === MINES_FIELD_STATE.UnOpened) {
        acc.push(index);
      }
      return acc;
    }, []);
  }

  resetNonSelectedBoxIndex() {
    this.nonSelectedBoxIndex = new Array(MAX_BOX_COUNT)
      .fill(MINES_FIELD_STATE.UnOpened)
      .map((el, index) => index);
  }

  setIsDoSpinStart(bool) {
    this.isDoSpinStart = bool;
  }

  setMinesBox(minesBox) {
    this.minesBox = this.generateUniqueIds(minesBox);
  }

  setDiamondsCount(count) {
    this.diamondsCount = count;
  }

  updateDiamondsCount() {
    this.diamondsCount -= 1;
  }

  setMinesCount(count, isFromInitial) {
    this.minesCount = count;
    if (count < MAX_BOX_COUNT) {
      this.setDiamondsCount(MAX_BOX_COUNT - count);
      if (count > 0 && !isFromInitial) {
        minesGameService.sendChangeBombCount(count);
      }
    }
  }

  handleIncreaseMinesCount(isIncrease) {
    const nextMinesCount = this.minesCount + (isIncrease ? 1 : -1);
    if (nextMinesCount > MINES_COUNT_LIMIT.maxBet) {
      this.setMinesCount(MINES_COUNT_LIMIT.maxBet);
    } else if (nextMinesCount < MINES_COUNT_LIMIT.minBet) {
      this.setMinesCount(MINES_COUNT_LIMIT.minBet);
    } else {
      this.setMinesCount(nextMinesCount);
    }
  }

  generateUniqueIds(minesBox) {
    return minesBox.map((item, index) => ({ result: item, id: index }));
  }

  handleAutoSelect() {
    const randomIndex = Math.floor(Math.random() * this.nonSelectedBoxIndex.length);
    const fieldIndex = this.nonSelectedBoxIndex[randomIndex];
    minesGameService.sendResultReport([fieldIndex]);
  }

  resetStore() {
    this.minesCount = DEFAULT_MINES_COUNT;
    this.diamondsCount = MAX_BOX_COUNT - DEFAULT_MINES_COUNT;
    this.minesBox = this.generateUniqueIds(
      new Array(MAX_BOX_COUNT).fill(MINES_FIELD_STATE.UnOpened)
    );
    this.isDoSpinStart = false;
    this.nonSelectedBoxIndex = new Array(MAX_BOX_COUNT)
      .fill(MINES_FIELD_STATE.UnOpened)
      .map((el, index) => index);
    this.cashOutWin = null;
    this.isAllowCashOut = false;
    this.openedFields = [];
    this.isBetsClosed = false;
    this.winAmount = null;
    this.winOdd = null;
    this.oddsByBombCount = [];
    this.activeOdd = null;
    this.oddsForView = [];
    this.isLose = false;
    this.selectedFieldsIndex = [];
    this.isAllowSendResultReport = false;
  }
}

export const minesBetStore = new MinesBetStore();
